<script setup lang="ts">
import { inject, ref, computed, nextTick } from 'vue';
import { AutoComplete } from "@progress/kendo-vue-dropdowns";
import { useCharacterDataStore } from "@/stores/character-data";
import { storeToRefs } from "pinia";
import { useAppDataStore } from "@/stores/app-data";
import { useUxData } from '@/stores/ux-data';
import ElementBrowser from '@/components/atoms/swade/ElementBrowser.vue';
import FinderItemPanel from '@/components/atoms/FinderItemPanel.vue';
import CharacterSourceFilter from '@/components/atoms/element-filters/CharacterSourceFilter.vue';
import FilterControl from '@/components/atoms/element-filters/FilterControl.vue';
import SkillAttributeFilter from '@/components/atoms/element-filters/SkillAttributeFilter.vue';
import { useElementPredicates, buildSecondaryChips, clearSecondaryChip } from '@/composables/useElementPredicates';
import { useElementBrowser } from '@/composables/useElementBrowser';
import { useCharacterSourceFilter } from '@/composables/useCharacterSourceFilter';
import { useCharacterWorldScope } from '@/composables/useCharacterWorldScope';
import { buildSkillPredicates } from '@/composables/buildPredicates';
import { useDebouncedSearch } from '@/composables/useDebouncedSearch';
import { skillAttributeLabel } from '@/appdata/element-label-formatters';
import { FINDER_POPUP } from '@/appdata/finder-popup';
import type { ApiDataProvider } from '@/api/ApiDataProvider';
import type { SettingElementInterface } from '@/types/items';
import type { SwadeSkillInterface } from '@/types/items';
import type { WorldInterface } from '@/types/worlds';
import type { AttributeNameType } from '@/types/characters';
import type { ColumnConfig, FilterChip } from '@/types/browser';

export interface SkillFinderFilters {
  world?: WorldInterface | null,
  source?: WorldInterface | null,
  linkedAttributes?: AttributeNameType[],
}

const emit = defineEmits(['skillSelected']);
const props = withDefaults(
    defineProps<{
      placeholder?: string,
      clearAfterSelect?: boolean,
      browsable?: boolean,
      placement?: 'start' | 'bottom' | 'end',
      closeOnAdd?: boolean,
      isAdded?: (element: SettingElementInterface) => boolean,
      canAdd?: (element: SettingElementInterface) => boolean,
      filters?: SkillFinderFilters,
    }>(),
    {
      clearAfterSelect: true,
      browsable: false,
      placement: 'end',
      closeOnAdd: false,
    }
)

const ux = useUxData();
const store = useAppDataStore();
const characterData = useCharacterDataStore();
storeToRefs(characterData)

const localStringPartial = ref('');

const buildPredicates = (stringPartial: string) =>
  buildSkillPredicates(
    {
      system: store.system,
      worlds: props.filters?.world ? [props.filters.world] : [],
      source: props.filters?.source ?? null,
    },
    {
      stringPartial,
      linkedAttributes: props.filters?.linkedAttributes ?? [],
    },
  );

const { onKeyUp } = useDebouncedSearch((value: string) => {
  localStringPartial.value = value;
  characterData.initializeSkillChoices(buildPredicates(value));
});

const onSkillSelected = (event: any) => {
  localStringPartial.value = '';

  emit('skillSelected', event);

  if (event.item && props.clearAfterSelect) {
    const self = window.document.querySelector('.skill-finder .k-clear-value');
    if (self) {
      self.click();
    }
  }
}

// --- Browser support (only active when browsable=true) ---

const autocompleteRef = ref<InstanceType<typeof AutoComplete> | null>(null);
const browserRef = ref<InstanceType<typeof ElementBrowser> | null>(null);

const provider = props.browsable ? inject<ApiDataProvider>('apiDataProvider')! : null;

// Scope the browser to the edited character's world (see useCharacterWorldScope).
const characterWorldScope = useCharacterWorldScope();

const { filters: browserFilters, predicates: browserPredicates, resetFilters } =
    props.browsable ? useElementPredicates('skills', undefined, { worlds: characterWorldScope.worlds }) : { filters: ref({}), predicates: computed(() => []), resetFilters: () => {} };

const defaultIsAdded = (element: SettingElementInterface): boolean => {
    return props.isAdded ? props.isAdded(element) : false;
};

const defaultCanAdd = (element: SettingElementInterface): boolean => {
    if (props.canAdd) return props.canAdd(element);
    return !defaultIsAdded(element);
};

const browser = props.browsable ? useElementBrowser({
    elementType: 'skills',
    predicates: browserPredicates,
    filterRefs: browserFilters as any,
    provider: provider!,
    closeOnAdd: props.closeOnAdd,
    onAdd: (element: SettingElementInterface) => {
        emit('skillSelected', { item: element, value: element.name });
        if (!props.clearAfterSelect) {
            nextTick(() => {
                const input = autocompleteRef.value?.$el?.querySelector('input') as HTMLInputElement;
                if (input) input.value = element.name;
            });
        }
    },
    isAdded: defaultIsAdded,
    canAdd: defaultCanAdd,
}) : null;

const onBrowserSelect = async (element: SettingElementInterface) => {
    if (!provider || !browser) return;
    browser.selectedElement.value = element;
    const fullElement = await provider.fetchElementDetail(element.uuid);
    if (fullElement && browser.selectedElement.value?.uuid === element.uuid) {
        browser.selectedElement.value = fullElement;
    }
};

// Linked-attribute filter inside the browser. Mirrors SkillList: SkillAttributeFilter
// stores the single-select choice as a one-element array (or clears it).
// useElementBrowser has no predicate watcher, so every write here refetches page 1.
const linkedAttributes = computed<string[]>({
    get: () => (browserFilters.value as any).linkedAttributes ?? [],
    set: (value: string[]) => {
        (browserFilters.value as any).linkedAttributes = value;
        browser?.fetch();
    },
});

const attributeChips = computed<FilterChip[]>(() =>
    buildSecondaryChips('skills', browserFilters.value as any),
);

const removeAttribute = (chip: FilterChip) => {
    clearSecondaryChip('skills', browserFilters.value as any, chip);
    browser?.fetch();
};

// Every loaded character gets the shared Source filter (see useCharacterSourceFilter); refetch
// only while this browser is open, so a source pick in one finder doesn't wake every other
// mounted one.
const sourceFilter = useCharacterSourceFilter(() => {
    if (browser?.isOpen.value) browser.fetch();
});

const filtersActive = computed(() =>
    attributeChips.value.length > 0
    || sourceFilter.active.value
    || ((browserFilters.value as any).stringPartial?.length ?? 0) > 0,
);

const clearBrowserFilters = () => {
    resetFilters();
    sourceFilter.clear();
    browser?.fetch();
};

const getAttributeLabel = (el: SettingElementInterface): string =>
    skillAttributeLabel(el, ux.labels?.swade?.attributes ?? {});

const columns: ColumnConfig[] = [
    {
      field: 'name',
      columnClass: ['name-column'],
      header: ux.labels?.swade?.data?.skills?.name ?? 'Name'
    },
    {
        field: 'linkedAttribute',
        header: ux.labels?.swade?.data?.skills?.linkedAttribute ?? 'Linked Attribute',
        formatter: (el: SettingElementInterface) => getAttributeLabel(el),
    },
    { field: 'source.name', header: ux.labels?.swade?.data?.skills?.source ?? 'Source' },
];

const openBrowser = () => {
    browser?.open();
    browserRef.value?.show();
};
</script>

<template>
  <div :class="browsable ? 'input-group flex-nowrap' : 'finder-passthrough'">
    <span v-if="browsable" data-bs-toggle="tooltip" data-bs-placement="right" :title="ux.labels?.swade?.data?.skills?.actions?.browse ?? ''">
      <button class="btn btn-light border-top-0 border-start-0 border-end-0 open-browser"
              type="button" @click="openBrowser">
        <span class="fa-light fa-browser"></span>
      </button>
    </span>
    <AutoComplete
        ref="autocompleteRef"
        class="skill-finder"
        text-field="name"
        :data-items="characterData.skills"
        :placeholder="props.placeholder"
        :item-render="'finder-item'"
        :popup-settings="FINDER_POPUP"
        @keyup="onKeyUp"
        @change="onSkillSelected"
    >
      <template #finder-item="{ props: itemProps }">
        <FinderItemPanel :item="itemProps">
          <template v-if="getAttributeLabel(itemProps.dataItem)" #badge>
            <span class="badge bg-info text-white">
              {{ getAttributeLabel(itemProps.dataItem) }}
              <span class="fa-light fa-link"></span>
            </span>
          </template>
        </FinderItemPanel>
      </template>
    </AutoComplete>
  </div>

  <ElementBrowser
      v-if="browsable && browser"
      ref="browserRef"
      :placement="placement"
      :close-on-add="closeOnAdd"
      :data-items="browser.dataItems.value"
      :columns="columns"
      :title="ux.labels?.swade?.data?.skills?.browse ?? 'Browse Skills'"
      :selected-element="browser.selectedElement.value"
      :is-added="defaultIsAdded"
      :can-add="defaultCanAdd"
      :loading="browser.loading.value"
      :metadata="browser.metadata.value"
      :links="browser.links.value"
      :filters-active="filtersActive"
      @add="browser.onAddClick"
      @select="onBrowserSelect"
      @search="browser.onSearch"
      @next="browser.onNextPage"
      @previous="browser.onPreviousPage"
      @clear="clearBrowserFilters"
      @close="browser.close"
  >
    <template #filters>
      <CharacterSourceFilter />
      <FilterControl
        :label="ux.labels?.swade?.data?.skills?.linkedAttribute ?? 'Attribute'"
        :chips="attributeChips"
        :multiple="false"
        @remove="removeAttribute"
      >
        <SkillAttributeFilter
          v-model:linked-attributes="linkedAttributes"
          :label="ux.labels?.swade?.data?.filters?.labels?.linkedAttribute ?? 'Attribute'"
          :placeholder="ux.labels?.swade?.data?.filters?.attributes ?? 'Attribute'"
        />
      </FilterControl>
    </template>

    <template #details="{ element }">
      <div v-if="(element as SwadeSkillInterface).linkedAttribute" class="small text-muted mb-2">
        <strong v-html="ux.labels?.swade?.data?.skills?.linkedAttribute ?? 'Linked Attribute'"></strong>:
        {{ getAttributeLabel(element) }}
      </div>
      <div v-if="element.permissions?.edit && element.description" class="small mb-3" v-html="element.description"></div>
      <div v-else-if="element.summary" class="small mb-3" v-html="element.summary"></div>
      <div v-else class="py-3 px-5 text-center">
        <span class="fa-light fa-fw fa-circle-notch fa-spin"></span>
      </div>
    </template>
  </ElementBrowser>
</template>
